import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAlert } from '../CustomAlert/AlertContext';
import EmptyState from '../common/EmptyState';
import { TableSkeleton } from '../common/Skeleton';

export default function HRExitManagement() {
  const [exits, setExits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifyingId, setNotifyingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { showConfirm, showAlert } = useAlert();

  // Offboarding checklist states
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [selectedExitForChecklist, setSelectedExitForChecklist] = useState(null);
  const [checklistItems, setChecklistItems] = useState({
    assetsReturned: false,
    accessRevoked: false,
    financeCleared: false,
    exitInterview: false
  });

  const fetchExits = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/employees/exits/all');
      setExits(res.data);
    } catch (err) {
      console.error('Failed to fetch exits:', err);
      showAlert('Failed to fetch employee exits.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExits();
  }, []);

  const handleNotify = async (id) => {
    try {
      setNotifyingId(id);
      const res = await api.post(`/api/employees/exits/${id}/notify`);
      showAlert(res.data.message || 'Notification dispatched successfully!', 'success');
    } catch (err) {
      console.error('Failed to send exit notification:', err);
      showAlert('Failed to send notification email.', 'error');
    } finally {
      setNotifyingId(null);
    }
  };

  const handleCompleteOffboarding = (exit) => {
    setSelectedExitForChecklist(exit);
    setChecklistItems({
      assetsReturned: false,
      accessRevoked: false,
      financeCleared: false,
      exitInterview: false
    });
    setShowChecklistModal(true);
  };

  const handleConfirmOffboarding = async () => {
    if (!selectedExitForChecklist) return;

    try {
      setDeletingId(selectedExitForChecklist.id);
      setShowChecklistModal(false);
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserId = currentUser.id;

      // Delete user via the standard admin endpoint
      await api.delete(`/api/auth/users/${selectedExitForChecklist.user_id}`, {
        headers: { 'x-user-id': currentUserId }
      });
      showAlert('Employee offboarded and deactivated successfully.', 'success');
      fetchExits();
    } catch (err) {
      console.error('Failed to complete offboarding:', err);
      showAlert('Failed to deactivate employee profile.', 'error');
    } finally {
      setDeletingId(null);
      setSelectedExitForChecklist(null);
    }
  };

  // Filter based on search query
  const filteredExits = exits.filter(exit => 
    exit.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exit.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (exit.project_name && exit.project_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Statistics
  const totalExits = exits.length;
  const pendingCount = exits.filter(e => e.exit_status === 'EXIT_PENDING').length;
  const approvedCount = exits.filter(e => e.exit_status === 'EXIT_APPROVED').length;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Employee Offboarding Tracker</h2>
          <p style={styles.subtitle}>Track pending clearances, coordinate settlements, and complete final offboarding steps.</p>
        </div>
      </div>

      {/* Stats row */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Total Active Exits</span>
          <span style={styles.statVal}>{totalExits}</span>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #ea580c' }}>
          <span style={styles.statLabel}>Awaiting BU Approval</span>
          <span style={styles.statVal}>{pendingCount}</span>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #16a34a' }}>
          <span style={styles.statLabel}>Approved & Ready for Offboarding</span>
          <span style={styles.statVal}>{approvedCount}</span>
        </div>
      </div>

      {/* Main card */}
      <div style={styles.tableCard}>
        {/* Toolbar */}
        <div style={styles.toolbar}>
          <h3 style={styles.tableTitle}>Offboarding Directory</h3>
          <input 
            type="text" 
            placeholder="Search by name, email, project..." 
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {loading ? (
          <TableSkeleton rows={5} columns={6} />
        ) : filteredExits.length === 0 ? (
          exits.length === 0 ? (
            <EmptyState
              icon="🛡️"
              title="No Pending Exit Clearances"
              description="All projects are clear. There are currently no employees in exit status or pending handover checklists."
            />
          ) : (
            <EmptyState
              icon="🔍"
              title="No Offboarding Records Found"
              description={`No employees in the exit pipeline matched your search "${searchQuery}".`}
              actionLabel="Clear Search"
              onAction={() => setSearchQuery('')}
            />
          )
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.trHeader}>
                  <th style={styles.th}>Employee Name</th>
                  <th style={styles.th}>Email Address</th>
                  <th style={styles.th}>Project</th>
                  <th style={styles.th}>Manager</th>
                  <th style={styles.th}>Last Working Day</th>
                  <th style={styles.th}>Exit Status</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExits.map(exit => (
                  <tr key={exit.id} style={styles.tr}>
                    <td style={styles.tdTitle}>{exit.full_name}</td>
                    <td style={styles.td}>{exit.email}</td>
                    <td style={styles.tdProject}>{exit.project_name || 'General Pool'}</td>
                    <td style={styles.td}>{exit.manager_name || 'Unassigned'}</td>
                    <td style={styles.tdDate}>
                      {exit.last_working_day ? new Date(exit.last_working_day).toLocaleDateString() : 'N/A'}
                    </td>
                    <td style={styles.td}>
                      <span style={
                        exit.user_deleted_at ? styles.badgeOffboarded :
                        exit.exit_status === 'EXIT_APPROVED' ? styles.badgeApproved : 
                        styles.badgePending
                      }>
                        {exit.user_deleted_at ? 'Offboarding Complete' :
                         exit.exit_status === 'EXIT_APPROVED' ? 'Approved by BU' : 
                         'Pending BU Approval'}
                      </span>
                    </td>
                    <td style={styles.tdAction}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        {exit.user_deleted_at ? (
                          <span style={{ color: 'var(--text-muted, #64748b)', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', padding: '6px 0' }}>
                            ✅ Offboarding Complete
                          </span>
                        ) : (
                          <>
                            <button 
                              style={styles.btnNotify} 
                              onClick={() => handleNotify(exit.id)}
                              disabled={notifyingId === exit.id}
                            >
                              {notifyingId === exit.id ? 'Notifying...' : 'Send Alert'}
                            </button>
                            
                            {exit.exit_status === 'EXIT_APPROVED' ? (
                              <button 
                                style={styles.btnDelete} 
                                onClick={() => handleCompleteOffboarding(exit)}
                                disabled={deletingId === exit.id}
                              >
                                {deletingId === exit.id ? 'Processing...' : 'Complete Offboarding'}
                              </button>
                            ) : (
                              <button 
                                style={styles.btnDeleteDisabled} 
                                disabled={true}
                                title="Awaiting BU head approval before offboarding"
                              >
                                Offboard (Awaiting BU)
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Offboarding Checklist Modal */}
      {showChecklistModal && selectedExitForChecklist && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Offboarding Checklist</h3>
              <button 
                onClick={() => {
                  setShowChecklistModal(false);
                  setSelectedExitForChecklist(null);
                }} 
                style={styles.modalCloseBtn}
              >
                ✕
              </button>
            </div>
            
            <p style={styles.modalDescription}>
              Please verify and check off the completed tasks for offboarding <b>{selectedExitForChecklist.full_name}</b> (<i>{selectedExitForChecklist.email}</i>).
            </p>

            <div style={styles.checklistContainer}>
              <label style={styles.checklistItem}>
                <input 
                  type="checkbox" 
                  checked={checklistItems.assetsReturned} 
                  onChange={e => setChecklistItems(prev => ({ ...prev, assetsReturned: e.target.checked }))}
                  style={styles.checkbox}
                />
                <div>
                  <span style={styles.checkText}>Asset Return Handover</span>
                  <span style={styles.checkSub}>Verify laptop, charger, ID card, keys, and accessories have been returned in good condition.</span>
                </div>
              </label>

              <label style={styles.checklistItem}>
                <input 
                  type="checkbox" 
                  checked={checklistItems.accessRevoked} 
                  onChange={e => setChecklistItems(prev => ({ ...prev, accessRevoked: e.target.checked }))}
                  style={styles.checkbox}
                />
                <div>
                  <span style={styles.checkText}>IT & System Access Revocation</span>
                  <span style={styles.checkSub}>Confirm deactivation of work email, Slack, GitHub, VPN, and any other system permissions.</span>
                </div>
              </label>

              <label style={styles.checklistItem}>
                <input 
                  type="checkbox" 
                  checked={checklistItems.financeCleared} 
                  onChange={e => setChecklistItems(prev => ({ ...prev, financeCleared: e.target.checked }))}
                  style={styles.checkbox}
                />
                <div>
                  <span style={styles.checkText}>Finance & Final Settlement Dues</span>
                  <span style={styles.checkSub}>Ensure final calculations, payroll settlement, and any remaining reimbursement claims are approved.</span>
                </div>
              </label>

              <label style={styles.checklistItem}>
                <input 
                  type="checkbox" 
                  checked={checklistItems.exitInterview} 
                  onChange={e => setChecklistItems(prev => ({ ...prev, exitInterview: e.target.checked }))}
                  style={styles.checkbox}
                />
                <div>
                  <span style={styles.checkText}>Exit Interview & Feedback Form</span>
                  <span style={styles.checkSub}>Confirm that the employee has completed the exit interview and submitted feedback.</span>
                </div>
              </label>
            </div>

            <div style={styles.modalFooter}>
              <span style={styles.modalFooterHint}>
                {Object.values(checklistItems).every(val => val === true) ? '✅ Ready to deactivate profile.' : '⚠️ Complete all items to proceed.'}
              </span>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  onClick={() => {
                    setShowChecklistModal(false);
                    setSelectedExitForChecklist(null);
                  }} 
                  style={styles.modalCancelBtn}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleConfirmOffboarding} 
                  disabled={!Object.values(checklistItems).every(val => val === true) || deletingId === selectedExitForChecklist.id}
                  style={Object.values(checklistItems).every(val => val === true) ? styles.modalConfirmBtn : styles.modalConfirmDisabledBtn}
                >
                  {deletingId === selectedExitForChecklist.id ? 'Processing...' : 'Complete Offboarding'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  header: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--text-primary, #0f172a)'
  },
  subtitle: {
    margin: '8px 0 0 0',
    color: 'var(--text-muted, #64748b)',
    fontSize: '14px'
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '20px'
  },
  statCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderLeft: '4px solid #4f46e5',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    backdropFilter: 'blur(10px)'
  },
  statLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-muted, #64748b)'
  },
  statVal: {
    fontSize: '28px',
    fontWeight: '800',
    color: 'var(--text-primary, #0f172a)'
  },
  tableCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px'
  },
  tableTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-primary, #0f172a)'
  },
  searchInput: {
    width: '100%',
    maxWidth: '300px',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid var(--border-color, #e2e8f0)',
    backgroundColor: 'var(--bg-page, #f8fafc)',
    color: 'var(--text-main, #0f172a)',
    fontSize: '14px',
    outline: 'none'
  },
  loader: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--text-muted, #64748b)'
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--text-muted, #64748b)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  trHeader: {
    borderBottom: '2px solid var(--border-color, #e2e8f0)'
  },
  th: {
    padding: '12px 16px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  tdTitle: {
    padding: '16px',
    fontWeight: '600',
    color: 'var(--text-primary, #0f172a)'
  },
  tdProject: {
    padding: '16px',
    fontWeight: '600',
    color: 'var(--accent-primary, #4f46e5)'
  },
  tdDate: {
    padding: '16px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary, #0f172a)'
  },
  td: {
    padding: '16px',
    fontSize: '14px',
    color: 'var(--text-main, #334155)'
  },
  tdAction: {
    padding: '16px',
    textAlign: 'right'
  },
  btnNotify: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    padding: '6px 14px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(59, 130, 246, 0.2)'
  },
  btnDelete: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    padding: '6px 14px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(239, 68, 68, 0.2)'
  },
  btnDeleteDisabled: {
    backgroundColor: '#f1f5f9',
    color: '#94a3b8',
    border: '1px solid #e2e8f0',
    padding: '6px 14px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'not-allowed'
  },
  badgePending: {
    backgroundColor: '#fef3c7',
    color: '#b45309',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgeApproved: {
    backgroundColor: '#dcfce7',
    color: '#15803d',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgeOffboarded: {
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block',
    border: '1px solid #e2e8f0'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modalContent: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '20px',
    padding: '32px',
    width: '100%',
    maxWidth: '560px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  modalTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '700',
    color: '#0f172a'
  },
  modalCloseBtn: {
    border: 'none',
    background: 'transparent',
    fontSize: '20px',
    color: '#64748b',
    cursor: 'pointer',
    padding: '4px'
  },
  modalDescription: {
    margin: 0,
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#475569'
  },
  checklistContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: 'rgba(241, 245, 249, 0.5)',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '20px'
  },
  checklistItem: {
    display: 'flex',
    gap: '14px',
    cursor: 'pointer',
    alignItems: 'flex-start',
    textAlign: 'left'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    border: '2px solid #cbd5e1',
    accentColor: '#4f46e5',
    cursor: 'pointer',
    marginTop: '3px'
  },
  checkText: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#0f172a'
  },
  checkSub: {
    display: 'block',
    fontSize: '11px',
    color: '#64748b',
    marginTop: '2px',
    lineHeight: '1.4'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px'
  },
  modalFooterHint: {
    fontSize: '12.5px',
    fontWeight: '600',
    color: '#64748b'
  },
  modalCancelBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #e2e8f0',
    color: '#64748b',
    padding: '10px 18px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '13.5px',
    cursor: 'pointer'
  },
  modalConfirmBtn: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '13.5px',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(239, 68, 68, 0.2)'
  },
  modalConfirmDisabledBtn: {
    backgroundColor: '#f1f5f9',
    color: '#94a3b8',
    border: '1px solid #e2e8f0',
    padding: '10px 18px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '13.5px',
    cursor: 'not-allowed'
  }
};
